import styled from 'styled-components';
import { useRouter } from 'next/router';
import Link from 'next/link';
import useSWR from 'swr';
import pick from 'lodash.pick';
import { useTranslations } from 'next-intl';

import Button from '@/components/button';
import Banner from '@/components/banner';
import BrandGrid from '@/components/brand-grid';
import HowItWorks from '@/components/how-it-works';
import BrowseMore from '@/components/browse-more';
import countries from '@data/countries';
import SEO from '@/components/seo';
import { brandsForCountry } from '@data/index';
import { shareImageForCountry } from '@/utils';
import config from '@/config';
import EarlyExit from '@/components/early-exit';
import { media } from '@/themes';
import Section from '@/components/section';

const Container = styled.div`
  margin: 0 auto;
  max-width: ${props => props.theme.maxContentWidth};
  margin-bottom: 2em;

  ${media.xl`
    padding: 0 1em;
  `};
`;

const BannerContent = styled.div`
  margin: auto;
  max-width: 40em;
`;

const Title = styled.h1`
  color: ${props => props.theme.colors.primary};
`;

const Subtitle = styled.h4`
  margin-bottom: 1em;
`;

const FlagLogo = styled.img`
  border-radius: 50%;
  width: 5em;
  height: 5em;
  object-fit: cover;
  margin-right: 1em;
`;

const GridTitle = styled.h2`
  margin: 0 0 2em;
`;

const fetcher = (...args) => fetch(...args).then(res => res.json()).then(result => result.groups.length > 0 ? result.groups[0].categories[0].brands.slice(0, 12) : []);
const countryfetcher = (...args) => fetch(...args).then(res => res.json()).then(result => result[0]);

const BrandsForCountry = ({ brands, country, router }) => {
  const t = useTranslations('country');
  const initiafallbackDatalData = brands;
  const matchingCountry = countries.find(c => c.slug === router.query.country);
  const countryId = matchingCountry ? matchingCountry.id : country.id;
  const apiUrl = `${config.commerceApiUrl}/search?countryId=${countryId}?minimal=true`;
  const { data } = useSWR(apiUrl, fetcher, { fallbackData: initiafallbackDatalData, revalidateOnMount: true });
  const seoTitle = t('seoTitle', { country: country.name });
  const url = `${config.rootUrl}${router.asPath}`;

  return <>
    <SEO
      title={seoTitle}
      url={url}
      imageUrl={shareImageForCountry(countryId)}
    />
    <Banner>
      {country &&
        <BannerContent>
          <FlagLogo src={`https://assets.bidali.com/flags/${country.id}.png`} />
          <div>
            <Title>{t('title', { country: country.name })}</Title>
            <Subtitle>{seoTitle}</Subtitle>
            <Link
              href={`/buy/${country.slug}`}
              passHref
              title={`View all products in ${country.name} to buy with Bitcoin, Ethereum, Litecoin, USDT, and more.`}>

              <Button primary>{t('browseAll')}</Button>

            </Link>
          </div>
        </BannerContent>
      }
    </Banner>
    <Section>
      {country && <GridTitle>{t('popularProductsIn', { country: country.name })}</GridTitle>}
      <BrandGrid brands={data} />
    </Section>
    <BrowseMore />
    <HowItWorks />
  </>;
};

const Index = ({ brands = [], country }) => {
  const router = useRouter();

  return (
    <EarlyExit
      Component={BrandsForCountry}
      condition={!router.query.country}
      brands={brands}
      country={country} />
  );
};

export async function getStaticPaths ({ locales }) {
  const paths = countries.map(country => ({
    params: { country: country.slug }
  }));

  for (let i = 0; i < locales.length; i++) {
    const locale = locales[i];

    countries.forEach(country => {
      locale !== 'en' && paths.push({
        params: { country: country.slug },
        locale
      })
    });
  }

  return {
    paths,
    fallback: false
  };
}

//TODO: Do we check if the country exists and if not we try to fetch from the API? Fallback: true
export async function getStaticProps ({ params, locale }) {
  let country = countries.find(c => c.slug === params.country);
  let brands = [];
  if (!country) {
    country = await countryfetcher(`${config.commerceApiUrl}/countries?slug=${params.country}`);
    const apiUrl = `${config.commerceApiUrl}/search?countryId=${country.id}&minimal=true`;
    brands = await fetcher(apiUrl);
  } else {
    brands = brandsForCountry(country.id);
  }

  brands = brands.map(b => {
    b.countrySlug = b.country.slug;
    b = pick(b, ['id', 'slug', 'countrySlug', 'backgroundColor', 'name', 'imageUrl']);
    return b;
  }).slice(0, 12);

  return {
    props: {
      brands,
      country,
      messages: require(`@/locales/${locale}.json`)
    }
  };
}

export default Index;
